import { useEffect, useRef } from "react";
import { Outlet, useLocation, useMatch } from "react-router-dom";
import type Lenis from "lenis";
import { CartProvider } from "@/lib/cart";
import { recordLanding } from "@/lib/attribution";
import { trackPageView, watchWhatsappClicks } from "@/lib/tracking";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { SignupPopup } from "./SignupPopup";
import { WhatsappFloat } from "./WhatsappFloat";

/** Height of the fixed header, so hash targets aren't hidden underneath it. */
const HEADER_OFFSET = 80;

/**
 * Smooth scrolling for the whole page (eased wheel/trackpad), plus hash- and
 * route-aware scroll restoration — all driven through Lenis when available.
 *
 * - Overrides the browser's native wheel scroll with Lenis' eased scroll.
 * - Disabled under prefers-reduced-motion (falls back to native scrolling).
 * - Lenis is loaded client-only (dynamic import) so it never runs during SSG.
 */
function useSmoothScroll() {
  const lenisRef = useRef<Lenis | null>(null);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    let cancelled = false;
    let instance: Lenis | null = null;

    void import("lenis").then(({ default: LenisCtor }) => {
      if (cancelled) return;
      instance = new LenisCtor({ duration: 1.1, smoothWheel: true });
      lenisRef.current = instance;
      const loop = (time: number) => {
        instance?.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    });

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      instance?.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    const lenis = lenisRef.current;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        if (lenis) {
          lenis.scrollTo(el, { offset: -HEADER_OFFSET });
        } else {
          el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
        }
        return;
      }
    }

    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname, hash]);
}

/** One page view per route, including the first: client-side navigations never reload the page. */
function usePageViews() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);
}

export function Layout() {
  useSmoothScroll();
  usePageViews();
  // Where this visit came from, for the order's attribution. Once per load.
  useEffect(() => recordLanding(), []);
  // Every WhatsApp link, counted from one listener (DEL-443).
  useEffect(() => watchWhatsappClicks(), []);
  const campaign = useMatch("/lp/:slug");
  // CartProvider wraps the whole shell, not just the shop pages: the header's
  // cart badge is on every page, and mounting the provider per-route would
  // refetch the cart on every navigation.
  return (
    <CartProvider>
      <TooltipProvider delayDuration={250}>
        {campaign ? (
          // A campaign page draws its own header (it knows whether it opens on
          // a hero), a reduced footer, and no popup: someone who just clicked
          // an ad is there to buy the offer.
          <Outlet />
        ) : (
          <>
            <Header />
            <Outlet />
            <Footer />
            <SignupPopup />
          </>
        )}
        <WhatsappFloat />
      </TooltipProvider>
    </CartProvider>
  );
}
